//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, TextField, Typography } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import { EthAddr } from '@shared/SchemaTypes'

import { useOtDispatch, useOtSelector } from '@react/redux/store'

import { PoolFlexOverviewCTO, PoolFlexOverviewCTOSchema } from '@shared/sharedTypes'
import { theme } from '@react/components/theme'

import { APR } from '@shared/conversions'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import ViewField from '@react/components/elements/ViewField'

import { setDailyInterestRateForFlexTx } from '@react/signedTxs/setDailyInterestRateForFlexTx'

export default function BorrowerSetFlexInterestRateForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props
  const dispatch = useOtDispatch()

  const poolOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolFlexOverviewCTO
  )

  const [interestRate, setInterestRate] = React.useState<number>(0)
  const [errorAPR, setErrorAPR] = React.useState<string | null>()

  useEffect(() => {
    if (poolOverviewCTO?.interestRate) {
      setInterestRate(Number(poolOverviewCTO?.interestRate) / 100)
    }
  }, [poolOverviewCTO])

  const handleOnChangeAPR = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newInterestRate = parseFloat(event.target.value)
    if (newInterestRate < 0) {
      setErrorAPR('APR cannot be less than 0')
    } else {
      setErrorAPR(null)
    }
    setInterestRate(newInterestRate)
  }

  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    borderRadius: '6px',
  }

  const handleSetInterestRate = (): void => {
    setIsSubmitted(true)
    dispatch(
      setDailyInterestRateForFlexTx({
        poolAddr,
        interestRate: Math.round(interestRate * 100).toString() as APR,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  return (
    <Box
      data-testid={BorrowerSetFlexInterestRateForm.name}
      px={0}
      py={0}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        direction: 'column',
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant={'h6'}>Update the Collateral Sheet with the assets for this Pool</Typography>
        </Grid>

        <Grid item xs={12}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.name}
          ></ViewField>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="bodyMuted">Interest Rate APR</Typography>
        </Grid>
        <Grid item xs={12}>
          <Box flexDirection={'column'} alignItems={'flex-start'}>
            <TextField
              id="interestRate"
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Interest Rate"
              value={interestRate}
              type={'number'}
              onChange={handleOnChangeAPR}
              error={!!errorAPR}
              helperText={errorAPR}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Button
            disabled={isSubmitted}
            sx={sx}
            onClick={() => {
              handleSetInterestRate()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Set Interest Rate</Typography>
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}
